import { Component, computed, inject, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, Observable, of, map } from 'rxjs';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { TextNormalizeDirective } from '../../../../../directives/text-normalize.directive';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { CompanyAuditInfoFeesService } from '../../../../../services/companies/company-audit-info-fees.service';
import { CompanyAuditInfoAndFeesDto, UpsertAuditInfoAndFeesRequest } from '../../../../../models/api/companyAuditInfoAndFeesDto';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { CompanyDetailsService } from '../../../../../services/companies/company-details.service';
import { LutService } from '../../../../../services/lut.service';
import { LookupDataService } from '../../../../../services/lookupDataService.service';
import { AuditFirm } from '../../../../../models/auditFirm';
import { AuditFeeCategory } from '../../../../../models/auditFeeCategory';
import { AuditSectionConfiguration } from '../../../../../models/auditSectionConfiguration';
import { Currency } from '../../../../../models/currency';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { CustomDateAdapter, DD_MM_YYYY_FORMAT } from '../../../../directors/director-form/director-form.component';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

interface FeeCategoryRow {
  category: AuditFeeCategory;
  firm1Control: FormControl<number | null>;
  firm2Control: FormControl<number | null>;
}

@Component({
  selector: 'app-audit1-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatAutocompleteModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    NumberFormatDirective,
    TextNormalizeDirective,
    KeywordInputComponent,
    ReadOnlyTabDirective
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_FORMAT }
  ],
  templateUrl: './audit1-tab.component.html',
  styleUrl: './audit1-tab.component.scss'
})
export class Audit1TabComponent implements OnInit, OnChanges, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private auditInfoFeesService = inject(CompanyAuditInfoFeesService);
  private companyKeywordService = inject(CompanyKeywordService);
  private companyDetailsService = inject(CompanyDetailsService);
  private lutService = inject(LutService);
  private lookupService = inject(LookupDataService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Audit1));

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  otherMeasurement: string = '';
  companyId: string | null = null;
  showYearWarning = false;
  isCarriedForward = false;
  showKeywords = true;
  companyCountryId: string | null = null;

  auditFirms: AuditFirm[] = [];
  filteredFirms1: AuditFirm[] = [];
  filteredFirms2: AuditFirm[] = [];
  firm1SearchControl = new FormControl('');
  firm2SearchControl = new FormControl('');
  currencies: Currency[] = [];
  allFeeCategories: AuditFeeCategory[] = [];
  sectionConfigurations: AuditSectionConfiguration[] = [];

  auditFeeRows: FeeCategoryRow[] = [];
  relatedFeeRows: FeeCategoryRow[] = [];
  taxFeeRows: FeeCategoryRow[] = [];
  otherFeeRows: FeeCategoryRow[] = [];

  firm1Data: CompanyAuditInfoAndFeesDto | null = null;
  firm2Data: CompanyAuditInfoAndFeesDto | null = null;

  otherFirm1Data: CompanyAuditInfoAndFeesDto | null = null;
  otherFirm2Data: CompanyAuditInfoAndFeesDto | null = null;

  form: FormGroup = this.fb.group({
    firm1: this.fb.group({
      auditFirmId: [''],
      auditor1: [''],
      auditor2: [''],
      currencyId: [''],
      signatureDate: [null as Date | null],
      state: [''],
      city: [''],
      auditFirmTenure: [null as number | null]
    }),
    firm2: this.fb.group({
      auditFirmId: [''],
      auditor1: [''],
      auditor2: [''],
      currencyId: [''],
      signatureDate: [null as Date | null],
      state: [''],
      city: [''],
      auditFirmTenure: [null as number | null]
    }),
    keywords: this.fb.group({
      main: [''],
      comment: ['']
    })
  });

  get firm1Group(): FormGroup {
    return this.form.get('firm1') as FormGroup;
  }

  get firm2Group(): FormGroup {
    return this.form.get('firm2') as FormGroup;
  }

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  get auditFeesTotal1(): number {
    return this.auditFeeRows.reduce((sum, row) => sum + (row.firm1Control.value || 0), 0);
  }

  get auditFeesTotal2(): number {
    return this.auditFeeRows.reduce((sum, row) => sum + (row.firm2Control.value || 0), 0);
  }

  get relatedFeesTotal1(): number {
    return this.relatedFeeRows.reduce((sum, row) => sum + (row.firm1Control.value || 0), 0);
  }

  get relatedFeesTotal2(): number {
    return this.relatedFeeRows.reduce((sum, row) => sum + (row.firm2Control.value || 0), 0);
  }

  get taxFeesTotal1(): number {
    return this.taxFeeRows.reduce((sum, row) => sum + (row.firm1Control.value || 0), 0);
  }

  get taxFeesTotal2(): number {
    return this.taxFeeRows.reduce((sum, row) => sum + (row.firm2Control.value || 0), 0);
  }

  get otherFeesTotal1(): number {
    return this.otherFeeRows.reduce((sum, row) => sum + (row.firm1Control.value || 0), 0);
  }

  get otherFeesTotal2(): number {
    return this.otherFeeRows.reduce((sum, row) => sum + (row.firm2Control.value || 0), 0);
  }

  formatNumber(value: number): string {
    return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  getOtherFeeValue(categoryId: string, firmIndex: 1 | 2): number | null {
    const data = firmIndex === 1 ? this.otherFirm1Data : this.otherFirm2Data;
    return data?.feeValues?.find(f => f.auditFeeCategoryId === categoryId)?.value ?? null;
  }

  getOtherFirmName(firmIndex: 1 | 2): string {
    const data = firmIndex === 1 ? this.otherFirm1Data : this.otherFirm2Data;
    if (!data?.auditFirmId) return '';
    return this.auditFirms.find(f => f.id === data.auditFirmId)?.name ?? '';
  }

  getOtherCurrencyName(firmIndex: 1 | 2): string {
    const data = firmIndex === 1 ? this.otherFirm1Data : this.otherFirm2Data;
    if (!data?.currencyId) return '';
    return this.currencies.find(c => c.id === data.currencyId)?.name ?? '';
  }

  filterFirms(searchText: string | AuditFirm | null): AuditFirm[] {
    if (!searchText || typeof searchText !== 'string' || searchText.length < 2) return [];
    const filter = searchText.toLowerCase();
    return this.auditFirms.filter(f => f.name.toLowerCase().includes(filter)).slice(0, 100);
  }

  onFirmSelected(firmIndex: 1 | 2, firm: AuditFirm): void {
    const group = firmIndex === 1 ? this.firm1Group : this.firm2Group;
    const searchControl = firmIndex === 1 ? this.firm1SearchControl : this.firm2SearchControl;
    const originalData = firmIndex === 1 ? this.firm1Data : this.firm2Data;
    group.get('auditFirmId')!.setValue(firm.id);
    searchControl.setValue(firm.name, { emitEvent: false });
    group.get('auditFirmId')!.markAsDirty();
    if (firm.id !== originalData?.auditFirmId) {
      group.get('auditFirmTenure')!.setValue(+this.selectedYear);
      group.get('auditFirmTenure')!.markAsDirty();
    }
    this.form.markAsDirty();
  }

  clearFirm(firmIndex: 1 | 2): void {
    const group = firmIndex === 1 ? this.firm1Group : this.firm2Group;
    const searchControl = firmIndex === 1 ? this.firm1SearchControl : this.firm2SearchControl;
    group.get('auditFirmId')!.setValue('');
    searchControl.setValue('');
    group.get('auditFirmId')!.markAsDirty();
    this.form.markAsDirty();
  }

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('audit1'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'audit1', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'audit1', year: this.selectedYear }));
    }

    this.firm1SearchControl.valueChanges.subscribe(val => {
      this.filteredFirms1 = this.filterFirms(val);
    });
    this.firm2SearchControl.valueChanges.subscribe(val => {
      this.filteredFirms2 = this.filterFirms(val);
    });

    // The firm autocompletes are standalone controls outside `form`, so ReadOnlyTabDirective
    // (which disables the form group) does not reach them — lock them here.
    if (this.isReadOnly()) {
      this.firm1SearchControl.disable({ emitEvent: false });
      this.firm2SearchControl.disable({ emitEvent: false });
    }

    this.patchKeywords();
    this.loadLookupData();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  onOtherMeasurementChange(year: string): void {
    this.otherMeasurement = year;
    if (!year || !this.companyId) {
      this.otherFirm1Data = null;
      this.otherFirm2Data = null;
      return;
    }
    this.loadOtherMeasurementData();
  }

  private loadOtherMeasurementData(): void {
    if (!this.companyId || !this.otherMeasurement) return;

    this.auditInfoFeesService.get(this.companyId, parseInt(this.otherMeasurement)).subscribe({
      next: resource => {
        const items = resource.state === 'present' ? (resource.data ?? []) : [];
        this.otherFirm1Data = items.find(d => d.indexNumber === 1) ?? null;
        this.otherFirm2Data = items.find(d => d.indexNumber === 2) ?? null;
      },
      error: () => {
        this.otherFirm1Data = null;
        this.otherFirm2Data = null;
      }
    });
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'audit1', year }));
    this.loadCompanyCountryAndData();
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  private getSaveDescription(): string {
    const keywordsGroup = this.form.get('keywords')!;
    const hasAuditChanges = this.firm1Group.dirty || this.firm2Group.dirty ||
      [...this.auditFeeRows, ...this.relatedFeeRows, ...this.taxFeeRows, ...this.otherFeeRows]
        .some(row => row.firm1Control.dirty || row.firm2Control.dirty);
    const hasKeywordChanges = keywordsGroup.dirty;

    if (hasAuditChanges && hasKeywordChanges) return 'audit info & fees and keywords';
    if (hasKeywordChanges) return 'keywords';
    return 'audit info & fees';
  }

  private performSave(): void {
    if (!this.companyId) return;

    const year = parseInt(this.selectedYear);
    const firm1Value = this.firm1Group.value;
    const firm2Value = this.firm2Group.value;

    const buildFeeValues = (rows: FeeCategoryRow[], firmIndex: 1 | 2) => {
      return rows
        .filter(row => {
          const val = firmIndex === 1 ? row.firm1Control.value : row.firm2Control.value;
          return val != null && val !== 0;
        })
        .map(row => ({
          auditFeeCategoryId: row.category.id,
          value: (firmIndex === 1 ? row.firm1Control.value : row.firm2Control.value)!
        }));
    };

    const allFeeRows = [...this.auditFeeRows, ...this.relatedFeeRows, ...this.taxFeeRows, ...this.otherFeeRows];

    const toUtcDate = (value: Date | string | null): string | null => {
      if (!value) return null;
      const date = value instanceof Date ? value : new Date(value);
      return isNaN(date.getTime()) ? null : date.toISOString();
    };

    const buildRequest = (firmValue: any, indexNumber: number): UpsertAuditInfoAndFeesRequest => ({
      year,
      indexNumber,
      auditFirmId: firmValue.auditFirmId || null,
      auditor1: firmValue.auditor1 || '',
      auditor2: firmValue.auditor2 || '',
      currencyId: firmValue.currencyId || null,
      signatureDate: toUtcDate(firmValue.signatureDate),
      state: firmValue.state || '',
      city: firmValue.city || '',
      auditFirmTenure: firmValue.auditFirmTenure || null,
      feeValues: buildFeeValues(allFeeRows, indexNumber as 1 | 2)
    });

    const hasFirmData = (firmValue: any, firmIndex: 1 | 2): boolean => {
      if (firmValue.auditFirmId || firmValue.auditor1 || firmValue.auditor2 ||
          firmValue.signatureDate || firmValue.state || firmValue.city || firmValue.auditFirmTenure) {
        return true;
      }
      return allFeeRows.some(row => {
        const val = firmIndex === 1 ? row.firm1Control.value : row.firm2Control.value;
        return val != null && val !== 0;
      });
    };

    const firm1HasData = hasFirmData(firm1Value, 1);
    const firm2HasData = hasFirmData(firm2Value, 2);

    // A firm cleared of all data but with an existing saved record for this year
    // must be deleted — otherwise the cleared change is silently dropped (no API
    // call is made) and the old value reappears on reload.
    const firm1NeedsDelete = !firm1HasData && !this.isCarriedForward && !!this.firm1Data;
    const firm2NeedsDelete = !firm2HasData && !this.isCarriedForward && !!this.firm2Data;

    const buildFirmCall = (
      firmValue: any,
      firmIndex: 1 | 2,
      firmHasData: boolean,
      needsDelete: boolean
    ): Observable<CompanyAuditInfoAndFeesDto | null> => {
      if (firmHasData) {
        return this.auditInfoFeesService.upsert(this.companyId!, buildRequest(firmValue, firmIndex));
      }
      if (needsDelete) {
        return this.auditInfoFeesService.delete(this.companyId!, year, firmIndex).pipe(map(() => null));
      }
      return of(null);
    };

    const calls: Observable<CompanyAuditInfoAndFeesDto | null>[] = [
      buildFirmCall(firm1Value, 1, firm1HasData, firm1NeedsDelete),
      buildFirmCall(firm2Value, 2, firm2HasData, firm2NeedsDelete)
    ];

    // Build keyword upsert requests
    const keywordsValue = this.form.get('keywords')!.value;
    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(keywordsValue, [
      { formField: 'main', type: KeywordType.AuditInfoAndFeesMain },
      { formField: 'comment', type: KeywordType.AuditInfoAndFeesComment }
    ], this.keywords);

    const hasAuditData = firm1HasData || firm2HasData || firm1NeedsDelete || firm2NeedsDelete;
    const hasKeywordData = keywordRequests.length > 0 || this.keywords.some(
      k => k.type === KeywordType.AuditInfoAndFeesMain || k.type === KeywordType.AuditInfoAndFeesComment
    );

    if (!hasAuditData && !hasKeywordData) {
      this.toast.info('No data to save');
      return;
    }

    this.spinnerService.show();

    const saveCalls: Observable<any>[] = [];

    if (hasAuditData) {
      saveCalls.push(forkJoin(calls));
    } else {
      saveCalls.push(of([null, null]));
    }

    const description = this.getSaveDescription();

    saveTabThenKeywords(saveCalls, this.companyKeywordService.upsert(this.companyId!, keywordRequests)).subscribe({
      next: ([auditResult, keywordsResult]) => {
        if (hasAuditData) {
          this.showYearWarning = false;
          this.isCarriedForward = false;
          const [data1, data2] = auditResult as (CompanyAuditInfoAndFeesDto | null)[];
          if (data1) this.firm1Data = data1;
          if (data2) this.firm2Data = data2;
          if (firm1NeedsDelete) this.firm1Data = null;
          if (firm2NeedsDelete) this.firm2Data = null;
        }
        this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  private patchKeywords(): void {
    const mainKeyword = this.keywords.find(k => k.type === KeywordType.AuditInfoAndFeesMain);
    const commentKeyword = this.keywords.find(k => k.type === KeywordType.AuditInfoAndFeesComment);
    this.form.get('keywords')!.patchValue({
      main: mainKeyword?.value ?? '',
      comment: commentKeyword?.value ?? ''
    }, { emitEvent: false });
    this.form.markAsPristine();
  }

  private loadLookupData(): void {
    this.spinnerService.show();

    this.lookupService.loadTable('currencies');
    this.lookupService.getTableStream<Currency>('currencies').subscribe(data => {
      if (data) this.currencies = data;
    });

    forkJoin([
      this.lutService.getAllAuditFirms(),
      this.lutService.getAuditFeeCategories(),
      this.lutService.getAuditSectionConfigurations()
    ]).subscribe({
      next: ([firms, categories, configs]) => {
        this.auditFirms = firms.sort((a, b) => a.name.localeCompare(b.name));
        this.allFeeCategories = categories;
        this.sectionConfigurations = configs;
        this.loadCompanyCountryAndData();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load lookup data');
        console.error('Lookup data error:', err);
      }
    });
  }

  private loadCompanyCountryAndData(): void {
    if (!this.companyId) {
      this.spinnerService.hide();
      return;
    }

    this.companyDetailsService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        this.companyCountryId = resource.data?.countryId ?? null;
        this.buildFeeRows();
        this.loadData();
      },
      error: () => {
        this.buildFeeRows();
        this.loadData();
      }
    });
  }

  private buildFeeRows(): void {
    const excludedCategoryIds = new Set(
      this.sectionConfigurations
        .filter(c => this.companyCountryId && c.countryId === this.companyCountryId)
        .map(c => c.auditFeeCategoryId)
    );

    const visibleCategories = this.allFeeCategories.filter(c => !excludedCategoryIds.has(c.id));

    const toRows = (primaryCategory: number): FeeCategoryRow[] =>
      visibleCategories
        .filter(c => c.primaryCategory === primaryCategory)
        .sort((a, b) => a.rank - b.rank)
        .map(c => ({
          category: c,
          firm1Control: new FormControl<number | null>(null),
          firm2Control: new FormControl<number | null>(null)
        }));

    this.auditFeeRows = toRows(1);
    this.relatedFeeRows = toRows(2);
    this.taxFeeRows = toRows(3);
    this.otherFeeRows = toRows(4);

    // Subscribe to fee control changes to mark form dirty
    [...this.auditFeeRows, ...this.relatedFeeRows, ...this.taxFeeRows, ...this.otherFeeRows].forEach(row => {
      row.firm1Control.valueChanges.subscribe(() => this.form.markAsDirty());
      row.firm2Control.valueChanges.subscribe(() => this.form.markAsDirty());
    });
  }

  private loadData(): void {
    if (!this.companyId) return;

    this.spinnerService.show();
    this.auditInfoFeesService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const items = resource.state === 'present'
          ? (resource.data ?? [])
          : (resource.carriedForward?.values ?? []);
        this.isCarriedForward = resource.state === 'carried-forward';
        this.showYearWarning = this.isCarriedForward || resource.state === 'absent';
        this.firm1Data = items.find(d => d.indexNumber === 1) ?? null;
        this.firm2Data = items.find(d => d.indexNumber === 2) ?? null;
        if (resource.state === 'absent') {
          this.resetForm();
        } else {
          this.patchForm();
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.firm1Data = null;
          this.firm2Data = null;
          this.resetForm();
        } else {
          this.toast.error('Error loading audit info & fees');
        }

        console.error('Audit info & fees error:', error);
      }
    });
  }

  private patchForm(): void {
    const patchFirm = (group: FormGroup, data: CompanyAuditInfoAndFeesDto | null) => {
      group.patchValue({
        auditFirmId: data?.auditFirmId ?? '',
        auditor1: data?.auditor1 ?? '',
        auditor2: data?.auditor2 ?? '',
        currencyId: data?.currencyId ?? '',
        signatureDate: data?.signatureDate ? new Date(data.signatureDate) : null,
        state: data?.state ?? '',
        city: data?.city ?? '',
        auditFirmTenure: data?.auditFirmTenure ?? null
      }, { emitEvent: false });
    };

    patchFirm(this.firm1Group, this.firm1Data);
    patchFirm(this.firm2Group, this.firm2Data);

    // Set display text for autocomplete inputs
    const firm1Name = this.auditFirms.find(f => f.id === this.firm1Data?.auditFirmId)?.name ?? '';
    const firm2Name = this.auditFirms.find(f => f.id === this.firm2Data?.auditFirmId)?.name ?? '';
    this.firm1SearchControl.setValue(firm1Name, { emitEvent: false });
    this.firm2SearchControl.setValue(firm2Name, { emitEvent: false });

    // Patch fee values
    const patchFees = (rows: FeeCategoryRow[], firm1Data: CompanyAuditInfoAndFeesDto | null, firm2Data: CompanyAuditInfoAndFeesDto | null) => {
      for (const row of rows) {
        const fee1 = firm1Data?.feeValues?.find(f => f.auditFeeCategoryId === row.category.id);
        const fee2 = firm2Data?.feeValues?.find(f => f.auditFeeCategoryId === row.category.id);
        row.firm1Control.setValue(fee1?.value ?? null);
        row.firm2Control.setValue(fee2?.value ?? null);
      }
    };

    patchFees(this.auditFeeRows, this.firm1Data, this.firm2Data);
    patchFees(this.relatedFeeRows, this.firm1Data, this.firm2Data);
    patchFees(this.taxFeeRows, this.firm1Data, this.firm2Data);
    patchFees(this.otherFeeRows, this.firm1Data, this.firm2Data);

    this.form.markAsPristine();
  }

  private resetForm(): void {
    this.firm1Group.reset({
      auditFirmId: '',
      auditor1: '',
      auditor2: '',
      currencyId: '',
      signatureDate: null,
      state: '',
      city: '',
      auditFirmTenure: null
    }, { emitEvent: false });

    this.firm2Group.reset({
      auditFirmId: '',
      auditor1: '',
      auditor2: '',
      currencyId: '',
      signatureDate: null,
      state: '',
      city: '',
      auditFirmTenure: null
    }, { emitEvent: false });
    this.firm1SearchControl.setValue('', { emitEvent: false });
    this.firm2SearchControl.setValue('', { emitEvent: false });

    [...this.auditFeeRows, ...this.relatedFeeRows, ...this.taxFeeRows, ...this.otherFeeRows].forEach(row => {
      row.firm1Control.setValue(null);
      row.firm2Control.setValue(null);
    });

    this.form.markAsPristine();
  }
}
